
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useCentralizedAuth } from '@/context/CentralizedAuthProvider';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const Navbar = () => {
  const { user, logout, isAuthenticated } = useCentralizedAuth();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <>
      <nav className="sticky top-0 w-full z-50 bg-white/80 backdrop-blur-md border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center text-brand-blue font-bold text-xl transition-all duration-300 hover:opacity-90"
              onClick={closeMenu}
            >
              <span className="font-medium">Career</span>
              <span className="font-bold">Guide</span>
              <span className="text-brand-lightBlue font-bold">Kerala</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-4">
              <Link 
                to="/" 
                className="text-gray-600 hover:text-brand-blue transition-colors duration-200 px-3 py-2 text-sm font-medium rounded-md"
              >
                Home
              </Link>
              <Link 
                to="/career-counselors" 
                className="text-gray-600 hover:text-brand-blue transition-colors duration-200 px-3 py-2 text-sm font-medium rounded-md"
              >
                Career Counselors
              </Link>
              {isAuthenticated && (
                <>
                  <Link 
                    to="/assessment" 
                    className="text-gray-600 hover:text-brand-blue transition-colors duration-200 px-3 py-2 text-sm font-medium rounded-md"
                  >
                    Assessment
                  </Link>
                  <Link 
                    to="/my-orders" 
                    className="text-gray-600 hover:text-brand-blue transition-colors duration-200 px-3 py-2 text-sm font-medium rounded-md"
                  >
                    My Orders
                  </Link>
                </>
              )}
              {isAuthenticated ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-brand-blue text-white">
                          {user?.name?.charAt(0) || 'U'}
                        </AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56" align="end" forceMount>
                    <div className="flex flex-col space-y-1 p-2">
                      <p className="text-sm font-medium leading-none">{user?.name}</p>
                      <p className="text-xs leading-none text-muted-foreground">
                        {user?.email}
                      </p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onSelect={() => navigate('/my-orders')}
                      className="cursor-pointer"
                    >
                      My Orders
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onSelect={handleLogout}
                      className="cursor-pointer"
                    >
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <div className="flex items-center space-x-3">
                  <Link to="/login">
                    <Button variant="ghost" size="sm">
                      Login
                    </Button>
                  </Link>
                  <Link to="/signup">
                    <Button variant="default" size="sm" className="bg-brand-blue hover:bg-brand-blue/90">
                      Sign Up
                    </Button>
                  </Link>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <Button 
                variant="ghost" 
                size="sm" 
                className="p-1" 
                onClick={toggleMenu}
                aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              >
                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden bg-white shadow-lg animate-slide-down">
          <div className="space-y-1 px-4 py-3">
            <Link 
              to="/" 
              className="block w-full text-left text-gray-600 hover:text-brand-blue px-3 py-2 rounded-md text-base font-medium"
              onClick={closeMenu}
            >
              Home
            </Link>
            <Link 
              to="/career-counselors" 
              className="block w-full text-left text-gray-600 hover:text-brand-blue px-3 py-2 rounded-md text-base font-medium"
              onClick={closeMenu}
            >
              Career Counselors
            </Link>
            {isAuthenticated && (
              <>
                <Link 
                  to="/assessment" 
                  className="block w-full text-left text-gray-600 hover:text-brand-blue px-3 py-2 rounded-md text-base font-medium"
                  onClick={closeMenu}
                >
                  Assessment
                </Link>
                <Link 
                  to="/my-orders" 
                  className="block w-full text-left text-gray-600 hover:text-brand-blue px-3 py-2 rounded-md text-base font-medium"
                  onClick={closeMenu}
                >
                  My Orders
                </Link>
              </>
            )}
            {isAuthenticated ? (
              <>
                <Button 
                  variant="ghost" 
                  className="w-full justify-start text-left text-gray-600 hover:text-brand-blue px-3 py-2 rounded-md text-base font-medium"
                  onClick={() => {
                    handleLogout();
                    closeMenu();
                  }}
                >
                  Logout
                </Button>
              </>
            ) : (
              <div className="flex flex-col space-y-2 pt-2">
                <Link to="/login" onClick={closeMenu}>
                  <Button variant="outline" className="w-full">
                    Login
                  </Button>
                </Link>
                <Link to="/signup" onClick={closeMenu}>
                  <Button variant="default" className="w-full bg-brand-blue hover:bg-brand-blue/90">
                    Sign Up
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
